import React from "react";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Brain, Activity, Zap, Heart, Users, Phone, Mail, MapPin, Clock, ArrowRight, Award } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext";

function NeurologyPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Neurology");

  const subspecialties = [
    {
      title: "Stroke & Cerebrovascular Disease",
      description: "Emergency stroke care, thrombolysis, and long-term stroke rehabilitation.",
      image: "/StrokeCare.jpg",
    },
    {
      title: "Epilepsy & Seizure Disorders",
      description: "Comprehensive diagnosis, medication, and surgical evaluation for seizures.",
      image: "/Epilepsy&SeizureDisorders.jpg",
    },
    {
      title: "Headache & Migraine Clinic",
      description: "Specialized care for chronic migraines and complex headache disorders.",
      image: "/Headache&MigraineClinic.jpg",
    },
    {
      title: "Movement Disorders",
      description: "Expert management of Parkinson's disease, tremors, and dystonia.",
      image: "/MovementDisorders.jpg",
    },
    {
      title: "Neuromuscular Diseases",
      description: "Diagnosis and treatment of peripheral neuropathy, myasthenia gravis, and muscular dystrophy.",
      image: "/Neuro-TraumaCare.jpg",
    },
    {
      title: "Dementia & Memory Disorders",
      description: "Comprehensive evaluation and care for Alzheimer's and other cognitive conditions.",
      image: "/NeurocriticalCare.jpg",
    },
  ];

  const treatments = [
    "Stroke Treatment & Thrombolysis",
    "Epilepsy Management & EEG",
    "Migraine & Headache Treatment",
    "Parkinson's Disease Management",
    "Peripheral Neuropathy Care",
    "Nerve Conduction Studies (NCS/EMG)",
    "Dementia Evaluation & Treatment",
    "Multiple Sclerosis Management",
  ];

  const whyChoose = [
    {
      icon: Brain,
      title: "Expert Neurologists",
      description: "Highly experienced neurologists with expertise in complex brain and nervous system disorders.",
    },
    {
      icon: Activity,
      title: "Advanced Diagnostic Technology",
      description: "State-of-the-art EEG, EMG, NCS, and neuroimaging facilities for accurate diagnosis.",
    },
    {
      icon: Zap,
      title: "Emergency Stroke Care",
      description: "24/7 stroke care with rapid thrombolysis and dedicated stroke management protocols.",
    },
    {
      icon: Heart,
      title: "Comprehensive Rehabilitation",
      description: "Integrated neuro-rehabilitation programs for faster and effective recovery.",
    },
    {
      icon: Users,
      title: "Multidisciplinary Team",
      description: "Coordinated care with neurosurgery, physiotherapy, and other specialties.",
    },
  ];

  const faqs = [
    {
      question: "What conditions does a neurologist treat?",
      answer:
        "Neurologists treat stroke, epilepsy, migraines, Parkinson's disease, multiple sclerosis, dementia, neuropathy, and other brain and nervous system disorders.",
    },
    {
      question: "What is the difference between a neurologist and a neurosurgeon?",
      answer:
        "Neurologists diagnose and treat neurological conditions with medication and therapy, while neurosurgeons perform surgical interventions on the brain and spine.",
    },
    {
      question: "Do you provide emergency stroke care?",
      answer:
        "Yes, MAMS Hospitals provides 24/7 emergency stroke care including clot-busting therapy (thrombolysis) and advanced stroke management.",
    },
    {
      question: "What tests are done for neurological conditions?",
      answer:
        "Common tests include EEG (brain wave test), MRI/CT scans, nerve conduction studies (NCS), EMG, and lumbar puncture depending on the condition.",
    },
  ];

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "MedicalClinic",
      "name": "MAMS Hospitals Neurology Department",
      "url": "https://mamshospitals.com/neurology",
      "logo": "https://mamshospitals.com/MAMS-Logo.png",
      "description": "MAMS Hospitals provides advanced neurology treatment in Hyderabad including stroke care, epilepsy treatment, migraine management, Parkinson's disease care, and neurological rehabilitation.",
      "medicalSpecialty": "Neurology",
      "telephone": "+91-7399999981",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Bachupally",
        "addressLocality": "Hyderabad",
        "addressRegion": "Telangana",
        "postalCode": "500090",
        "addressCountry": "IN",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What conditions does a neurologist treat?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Neurologists treat stroke, epilepsy, migraines, Parkinson's disease, multiple sclerosis, dementia, and other brain and nervous system disorders.",
          },
        },
        {
          "@type": "Question",
          "name": "Do you provide emergency stroke care?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, MAMS Hospitals provides 24/7 emergency stroke care including thrombolysis and advanced stroke management.",
          },
        },
      ],
    },
  ];

  return (
    <>
      <SEO
        title="Best Neurology Hospital in Hyderabad | Stroke & Brain Specialists | MAMS Hospitals"
        description="MAMS Hospitals offers advanced neurology treatment in Hyderabad including stroke care, epilepsy treatment, migraine management, Parkinson's disease care, and neurological rehabilitation."
        keywords="best neurologist Hyderabad, neurology hospital Hyderabad, stroke treatment Hyderabad, epilepsy treatment Hyderabad, migraine treatment Hyderabad, Parkinson's disease treatment Hyderabad, neurology hospital Bachupally, MAMS Hospitals Neurology"
        schema={schema}
      canonicalUrl="https://mamshospitals.com/departments/neurology"
      />
      <div className="flex flex-col min-h-screen">

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
          <div className="container mx-auto px-0 max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  Best Neurology Hospital in Hyderabad
                </div>
                <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                  Advanced Neurological Care with Precision & Compassion
                </h1>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  MAMS Hospitals is recognized as one of the Best Neurology Hospitals in Hyderabad, offering
                  comprehensive diagnosis and treatment for all brain and nervous system disorders with advanced
                  technology and expert care.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>
                  <a href="tel:+917399999981">
                    <Button className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8" variant="outline" size="lg">
                      <Phone className="mr-2 h-5 w-5" />
                      Call Us
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl z-10">
                  <div className="text-3xl font-bold">24/7</div>
                  <div className="text-sm">Stroke Care</div>
                </div>
                <img src="/Neurology.jpg" alt="Neurology at MAMS Hospital" className="rounded-2xl shadow-2xl w-full" />
              </div>
            </div>
          </div>
        </section>

        {/* Sub-Specialties */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-0 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sub-Specialties in Neurology</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">Comprehensive neurological care across multiple specializations</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subspecialties.map((specialty, index) => (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-teal-500">
                  <CardContent className="p-0">
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img src={specialty.image || "/placeholder.svg"} alt={specialty.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{specialty.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{specialty.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Treatments */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto px-0 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Treatments & Procedures</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">Advanced neurological treatments and diagnostic services</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {treatments.map((treatment, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                  <Activity className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700 font-medium">{treatment}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-0 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose MAMS Hospitals for Neurology</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">Trusted neurological excellence with patient-centered care</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyChoose.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                        <Icon className="h-7 w-7 text-teal-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Specialist */}
        {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* FAQs */}
        <section className="py-16 md:py-20 px-0 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-6">
              {faqs.map((faq, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact & Appointment Section */}
        <section className="py-16 px-0 bg-apple-teal text-white">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
              <p className="text-white/90 max-w-2xl mx-auto">
                Your health is our top priority. Book an appointment today with Hyderabad's trusted neurologists at MAMS Hospitals.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
                <CardContent className="p-6 text-center">
                  <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                  <h3 className="font-bold text-white mb-2">Call Us</h3>
                  <a href="tel:+917399999981" className="text-white/90 hover:text-white">+91-7399999981</a>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
                <CardContent className="p-6 text-center">
                  <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                  <h3 className="font-bold text-white mb-2">Email Us</h3>
                  <a href="mailto:info@mamshospitals.com" className="text-white/90 hover:text-white">info@mamshospitals.com</a>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
                <CardContent className="p-6 text-center">
                  <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                  <h3 className="font-bold text-white mb-2">Visit Us</h3>
                  <p className="text-white/90">Bachupally, Miyapur</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Emergency Banner */}
        <section className="py-8 px-0 bg-red-600 text-white">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Clock className="h-8 w-8" />
                <div>
                  <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                  <p className="text-white/90">Fully equipped emergency department</p>
                </div>
              </div>
              <a href="tel:+917399999981">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Emergency
                </Button>
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}

export default NeurologyPage;
